// hooks/useChargingSessionById.js
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useWebSocketContext } from "../contexts/WebSocketProvider";

export default function useChargingSessionById() {
  const { sessionId } = useParams(); // get sessionId from route params
  const [session, setSession] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const { send, isConnected, registerHandler } = useWebSocketContext();
  const mountedRef = useRef(true);
  const retryTimeoutRef = useRef(null);

  const fetchSession = useCallback(async (attempt = 0) => {
    if (!mountedRef.current || !sessionId || !isConnected) return;

    setIsLoading(true);
    setError("");
    setMessage("");

    try {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }

      const response = await send("cms_get_charging_session_by_id", { charging_session_id: sessionId });
      console.log(`Charging session ${sessionId}:`, response);

      if (!response || response.type !== "cms_get_charging_session_by_id_response") {
        throw new Error("Invalid response from server");
      }

      if (!response.data) {
        throw new Error("Session not found");
      }

      setSession(response.data);
      setMessage(response.message || "Session fetched successfully");
    } catch (err) {
      setError(err.message);

      if (mountedRef.current) {
        const delay = Math.min(1000 * Math.pow(2, attempt), 30000);
        retryTimeoutRef.current = setTimeout(() => {
          fetchSession(attempt + 1);
        }, delay);
      }
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [sessionId, isConnected, send]);

  useEffect(() => {
    mountedRef.current = true;

    // automatically fetch session once WebSocket is connected
    if (sessionId && isConnected) {
      fetchSession();
    }

    const unregister = registerHandler(`charging_session_update_${sessionId}`, (msg) => {
      if (msg.data && mountedRef.current) {
        setSession(msg.data);
      }
    });

    return () => {
      mountedRef.current = false;
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
      unregister();
    };
  }, [sessionId, fetchSession, registerHandler, isConnected]);

  return { session, isLoading, error, message, refetch: () => fetchSession(0) };
}
